import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, makeStyles, TextField } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { showToast } from '@klpkonsern/ui';
import { Block, KlpModal, Typography } from '@klp/design';
import { FileUploadDynamic } from '@klp/file/components';
import type { FileObject } from '@klp/file/classes';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { usePostProsjektAvfallMutation } from '../services/rtkApi';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
    editableField: {
        '& .MuiInputBase-root': {
            color: theme.palette.text.primary,
        },
        '& .Mui-disabled': {
            cursor: 'not-allowed',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
            fontSize: '1.5rem',
        },
    },
    fileCard: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: 'row',
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    fileInfo: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    actionButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
    },
}));

type FileDto = {
    name: string;
    type?: string;
    content?: string;
};

type ProsjektAvfallBase = {
    id: number | null;
    prosessInstansId: string;
    prosjektnavn: string;
};

type ProsjektAvfallUploadProps = {
    prosesser: Array<ProsjektAvfallBase>;
};

export function ProsjektAvfallUpload({ prosesser }: ProsjektAvfallUploadProps) {
    const { t } = useTranslation();
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<FileDto>();
    const [selectedProsess, setSelectedProsess] =
        useState<ProsjektAvfallBase>();
    const [postProsjektAvfall] = usePostProsjektAvfallMutation();

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
    };

    const handleAddFile = (file: FileObject) => {
        const fileDto = {
            name: file.fileName,
            type: file.type,
            content: file.fileContent,
        };

        setSelectedFile(fileDto);
    };

    const handleUpload = () => {
        if (!selectedFile || !selectedProsess) return;

        postProsjektAvfall({
            prosessInstansId: selectedProsess.prosessInstansId,
            file: selectedFile,
        })
            .unwrap()
            .then(() => {
                showToast({
                    body: t(
                        'sustainability.prosjektAvfall.uploadRapportSuccess',
                    ),
                    type: 'success',
                });
            });
        setSelectedProsess(undefined);
        setSelectedFile(undefined);
        setOpen(false);
    };

    return (
        <Box>
            <Button variant="contained" color="secondary" onClick={handleOpen}>
                {t('sustainability.prosjektAvfall.uploadRapport')}
            </Button>
            <KlpModal
                isOpen={open}
                onClose={handleClose}
                translation="sustainability.prosjektAvfall.uploadRapport"
            >
                <Block marginTop="fat" marginBottom="compact">
                    <Autocomplete<ProsjektAvfallBase>
                        id="prosjekt-avfall-select"
                        selectOnFocus={true}
                        clearOnBlur={true}
                        handleHomeEndKeys={true}
                        options={prosesser}
                        getOptionLabel={(option) => option.prosjektnavn}
                        getOptionSelected={(option, value) =>
                            option.id === value.id
                        }
                        value={selectedProsess}
                        onChange={(_event, newValue) =>
                            setSelectedProsess(newValue ?? undefined)
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth={true}
                                className={classes.editableField}
                                required={true}
                                variant="outlined"
                                label={t(
                                    'sustainability.prosjektAvfall.projectLabel',
                                )}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        )}
                    />
                    {selectedProsess?.id && (
                        <Block marginTop="compact">
                            <Alert severity="warning">
                                {t(
                                    'sustainability.prosjektAvfall.projectWarning',
                                )}
                            </Alert>
                        </Block>
                    )}
                </Block>
                <Block marginBottom="fat">
                    <Typography variant="h6">
                        {t('sustainability.prosjektAvfall.sluttrapportLabel')}
                    </Typography>
                    {selectedFile && (
                        <Card className={classes.fileCard}>
                            <Box className={classes.fileInfo}>
                                <Typography variant="body1" noWrap={true}>
                                    {selectedFile.name}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    noWrap={true}
                                >
                                    {selectedFile.type}
                                </Typography>
                            </Box>
                        </Card>
                    )}
                    <Block className={classes.actionButtonContainer}>
                        <FileUploadDynamic
                            mimeTypes="application/pdf"
                            queueName="vedlegg"
                            id="vedlegg-uploader"
                            fileUploadCallback={handleAddFile}
                        >
                            <Button variant="outlined" endIcon={<Add />}>
                                {t('sustainability.prosjektAvfall.selectFile')}
                            </Button>
                        </FileUploadDynamic>
                    </Block>
                </Block>
                <Button
                    disabled={!selectedFile || !selectedProsess}
                    variant="contained"
                    color="secondary"
                    onClick={handleUpload}
                >
                    {t('sustainability.prosjektAvfall.lastOppRapport')}
                </Button>
            </KlpModal>
        </Box>
    );
}
